// Servicio para manejar los logs de actividades de usuarios
import { db, auth } from '../firebase';
import { collection, addDoc, getDocs, query, where, orderBy, limit } from 'firebase/firestore';

/**
 * Tipos de acciones que se pueden registrar en los logs
 */
export const LOG_ACTIONS = {
  CREATE: 'crear',
  UPDATE: 'editar',
  DELETE: 'eliminar',
  VIEW: 'visualizar',
  APPROVE: 'aprobar',
  REJECT: 'rechazar',
  ACTA_AUTO_APPROVED: 'auto_aprobar'
};

/**
 * Registra una acción en el sistema de logs
 * @param {string|Object} userIdOrUser - ID del usuario o 'system' para logs automáticos, o objeto usuario completo
 * @param {string} action - Tipo de acción (crear, editar, eliminar)
 * @param {string} description - Descripción de la acción
 * @param {Object} details - Detalles adicionales de la acción
 * @returns {Promise<string>} - ID del log creado
 */
export const logUserAction = async (userIdOrUser, action, description, details = {}) => {
  try {
    let logData;
    
    if (userIdOrUser === 'system') {
      // Log del sistema
      logData = {
        userId: 'system',
        userEmail: 'system@gestor-actas.com',
        userName: 'Sistema Automático',
        action,
        description,
        details,
        timestamp: new Date()
      };
    } else {
      // Log de usuario
      const user = typeof userIdOrUser === 'string' ? auth.currentUser : userIdOrUser;
      
      if (!user && typeof userIdOrUser !== 'object') {
        console.error('No hay usuario autenticado para registrar la acción');
        return null;
      }
      
      logData = {
        userId: user?.uid || userIdOrUser?.uid,
        userEmail: user?.email || userIdOrUser?.email,
        userName: user?.displayName || user?.email || userIdOrUser?.displayName || userIdOrUser?.email,
        action,
        description,
        details,
        timestamp: new Date()
      };
    }
    
    const docRef = await addDoc(collection(db, 'logs'), logData);
    return docRef.id;
  } catch (error) {
    console.error('Error al registrar log:', error);
    return null;
  }
};

/**
 * Obtiene los logs del sistema con opciones de filtrado
 * @param {Object} filters - Filtros a aplicar
 * @param {string} filters.userId - Filtrar por ID de usuario
 * @param {string} filters.action - Filtrar por tipo de acción
 * @param {string} filters.resourceType - Filtrar por tipo de recurso
 * @param {string} filters.resourceId - Filtrar por ID de recurso
 * @param {Date} filters.startDate - Fecha de inicio para filtrar
 * @param {Date} filters.endDate - Fecha de fin para filtrar
 * @param {number} maxResults - Número máximo de resultados a devolver
 * @returns {Promise<Array>} - Array con los logs encontrados
 */
export const getLogs = async (filters = {}, maxResults = 100) => {
  try {
    let q = collection(db, 'logs');
    const queryConstraints = [];
    
    // Aplicar filtros si existen
    if (filters.userId) {
      queryConstraints.push(where('userId', '==', filters.userId));
    }
    
    if (filters.action) {
      queryConstraints.push(where('action', '==', filters.action));
    }
    
    if (filters.resourceType) {
      queryConstraints.push(where('resourceType', '==', filters.resourceType));
    }
    
    if (filters.resourceId) {
      queryConstraints.push(where('resourceId', '==', filters.resourceId));
    }
    
    if (filters.startDate && filters.endDate) {
      queryConstraints.push(where('timestamp', '>=', filters.startDate));
      queryConstraints.push(where('timestamp', '<=', filters.endDate));
    } else if (filters.startDate) {
      queryConstraints.push(where('timestamp', '>=', filters.startDate));
    } else if (filters.endDate) {
      queryConstraints.push(where('timestamp', '<=', filters.endDate));
    }
    
    // Ordenar por fecha descendente (más reciente primero)
    queryConstraints.push(orderBy('timestamp', 'desc'));
    
    // Limitar resultados
    queryConstraints.push(limit(maxResults));
    
    // Ejecutar la consulta
    q = query(q, ...queryConstraints);
    const querySnapshot = await getDocs(q);
    
    const logs = [];
    querySnapshot.forEach((doc) => {
      logs.push({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp ? doc.data().timestamp.toDate() : new Date() // Convertir timestamp a Date si existe
      });
    });
    
    return logs;
  } catch (error) {
    console.error('Error al obtener logs:', error);
    return [];
  }
};
